import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Signed links from every e-mail (?e=&t=):
 *   /baja         → unsubscribe from marketing (one click; e-mail 17 confirms)
 *   /suscripcion  → subscribe again
 *   /preferencias → see and change the choice
 */
const EmailPreferencesPage = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const e = searchParams.get('e') || '';
  const tk = searchParams.get('t') || '';
  const mode = pathname.startsWith('/baja') ? 'unsubscribe' : pathname.startsWith('/suscripcion') ? 'subscribe' : 'preferences';
  const [prefs, setPrefs] = useState(null); // {email, marketing}
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const change = async (action) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/email/${action}`, { e, t: tk });
      setPrefs(r.data);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!e || !tk) { setError(true); return; }
    if (mode === 'preferences') {
      axios.get(`${API}/email/preferences`, { params: { e, t: tk } }).then((r) => setPrefs(r.data)).catch(() => setError(true));
    } else {
      change(mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, e, tk]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FAF8F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">{t('emailPages.prefsTitle')}</CardTitle>
          {prefs && <CardDescription>{prefs.email}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-800">{t('emailPages.linkInvalid')}</p>
            </div>
          )}
          {!error && !prefs && <p className="text-sm text-gray-600">{t('emailPages.loading')}</p>}
          {prefs && (
            <div className="space-y-4" data-testid="email-prefs">
              <div className={`rounded-lg p-5 text-center border ${prefs.marketing ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <CheckCircle className={`w-10 h-10 mx-auto mb-2 ${prefs.marketing ? 'text-green-600' : 'text-gray-500'}`} />
                <p className="font-semibold">{prefs.marketing ? t('emailPages.subscribed') : t('emailPages.unsubscribed')}</p>
                <p className="text-sm text-gray-600 mt-1">{t('emailPages.transactionalNote')}</p>
              </div>
              <Button disabled={busy} variant="outline" className="w-full"
                      onClick={() => change(prefs.marketing ? 'unsubscribe' : 'subscribe')}>
                {prefs.marketing ? t('emailPages.unsubscribeButton') : t('emailPages.subscribeButton')}
              </Button>
              <Link to="/" className="block text-center text-sm text-[#be1f1a] hover:underline">{t('emailPages.backHome')}</Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailPreferencesPage;
